import {
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SeahorseTemplateDirective } from '@seahorse/common';
import { BaseControl } from '../base.control';

@Component({
  selector: 'sh-autocomplete',
  templateUrl: './autocomplete.control.html',
  styleUrls: ['./autocomplete.control.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoCompleteControl),
      multi: true,
    },
    {
      provide: BaseControl,
      useExisting: forwardRef(() => AutoCompleteControl),
    },
  ],
})
export class AutoCompleteControl extends BaseControl<
  string | number | (string | number)[]
> {
  private _lastValidValue?: string | number | (string | number)[];
  private _inputValue!: string | number | (string | number)[];

  @ContentChildren(SeahorseTemplateDirective)
  templates?: QueryList<SeahorseTemplateDirective>;

  get footerTemplate() {
    return this.templates?.find((x) => x.type === 'footer')?.templateRef;
  }

  get headerTemplate() {
    return this.templates?.find((x) => x.type === 'header')?.templateRef;
  }

  get itemTemplate() {
    return this.templates?.find((x) => x.type === 'item')?.templateRef;
  }
  @Input() appendTo = 'self';
  @Input() autoHighlight = true;
  @Input() class?: string;
  @Input() forceSelection = true;
  @Input() id?: string;
  @Input() items: any[] = [];
  @Input() itemLabel?: string;
  @Input() itemValue?: string;
  @Input() multiple = false;
  @Input() readonly = false;
  @Input() openOnFocus = false;
  @Input() panelStyleClass?: string;
  @Input() minLength = 1;
  @Input() isDisabled = false;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onChange = new EventEmitter<string | number | null>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSearch = new EventEmitter<string>();

  get inputValue(): any {
    const itemValue = this.itemValue;

    if (!itemValue) {
      return this._inputValue;
    }

    if (this.multiple) {
      const ids = (Array.isArray(this._inputValue) ? this._inputValue : []) as (
        | string
        | number
      )[];

      return ids
        .map((id) => this.items?.find((i) => i?.[itemValue] === id))
        .filter(Boolean);
    }

    return this.items?.find((i) => i?.[itemValue] === this._inputValue);
  }
  set inputValue(value: any) {
    const itemValue = this.itemValue;

    if (this.multiple) {
      const arr = Array.isArray(value) ? value : [];

      const ids = itemValue
        ? arr.map((v) => v?.[itemValue]).filter(Boolean)
        : arr;

      this._inputValue = ids;
      this._lastValidValue = ids;

      this.emitOnChange(ids);
      this.onChange.emit(ids as any);
      return;
    }

    if (value !== this._inputValue) {
      if (value && this.itemValue !== undefined && this.itemValue !== null) {
        this._inputValue = value[this.itemValue];
        if (this._inputValue) {
          this._lastValidValue = this._inputValue;
        }
        this.emitOnChange(value[this.itemValue]);
        this.onChange.emit(value[this.itemValue]);
      } else {
        this._inputValue = value;
        if (this._inputValue) {
          this._lastValidValue = this._inputValue;
        }
        this.emitOnChange(value);
        this.onChange.emit(value);
      }
    }
  }

  blurred() {
    if (this.multiple) {
      return;
    }

    if (!this.inputValue && this._lastValidValue) {
      this._inputValue = this._lastValidValue;
      this.emitOnChange(this._lastValidValue as any);
    }
  }

  search(evt: any) {
    const q = evt?.query;
    this.onSearch.emit(q);
  }

  override writeValue(value: any): void {
    super.writeValue(value);
    this._inputValue = value;
    this._lastValidValue = value;
  }
}
